/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import React, { useEffect, useRef } from 'react';
import { Alert, Button, message, Modal, Space, Typography } from 'antd';
import {
  CopyOutlined,
  ExclamationCircleOutlined,
  LinkOutlined,
} from '@ant-design/icons';
import { useIntl } from 'react-intl';
import { queryCache, useMutation } from 'react-query';
import { useStyles } from '~src/hooks/useStyles';
import { TenderRegistrationVM } from '~src/types/db';
import { changeTenderState, unpublishTenderPublic } from '~src/services/api';
import { QueryKeys } from '~src/hooks/api-hooks/queryKeys';

const { Text, Title } = Typography;

export type TenderPublicTabProps = {
  projectId?: number;
  tenderId: number | undefined;
  tenderRegistration?: TenderRegistrationVM;
};

export const TenderPublicTab: React.FC<TenderPublicTabProps> = ({
  projectId,
  tenderId,
  tenderRegistration,
}) => {
  const intl = useIntl();
  const styles = useStyles(styleFn);
  const linkTextareaRef = useRef<HTMLTextAreaElement | null>(null);

  const publicData = tenderRegistration?.public;
  const publicLink = publicData?.link;

  const hasPublicResource = !!publicData;
  const isPublicDeactivated = !!publicData?.removed_at;
  const isPublicActive = hasPublicResource && !isPublicDeactivated;

  const invalidateTenderRegistration = () => {
    queryCache.invalidateQueries('tenders');
    queryCache.invalidateQueries([QueryKeys.tenderAdminSharedTender]);
    queryCache.invalidateQueries([QueryKeys.tenderRegistration]);
  };

  const [publishTender, { isLoading: isPublishing }] = useMutation(
    changeTenderState,
    {
      onSuccess: () => {
        invalidateTenderRegistration();
        message.success(intl.formatMessage({ id: 'success' }));
      },
      onError: () => {
        message.error(intl.formatMessage({ id: 'error' }));
      },
    }
  );

  const [unpublishTender, { isLoading: isUnpublishing }] = useMutation(
    unpublishTenderPublic,
    {
      onSuccess: () => {
        invalidateTenderRegistration();
        message.success(intl.formatMessage({ id: 'success' }));
      },
      onError: () => {
        message.error(intl.formatMessage({ id: 'error' }));
      },
    }
  );

  const isLoading = isPublishing || isUnpublishing;
  const actionsDisabled = !projectId || !tenderId || isLoading;

  useEffect(() => {
    if (!publicLink) return;

    const timeout = window.setTimeout(() => {
      linkTextareaRef.current?.focus();
      linkTextareaRef.current?.select();
    }, 100);

    return () => window.clearTimeout(timeout);
  }, [publicLink]);

  const handleCopyLink = async () => {
    if (!publicLink) return;

    linkTextareaRef.current?.focus();
    linkTextareaRef.current?.select();

    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(publicLink);
      } else {
        document.execCommand('copy');
      }

      message.success(intl.formatMessage({ id: 'copied-to-clipboard' }));
    } catch (e) {
      message.error(intl.formatMessage({ id: 'error' }));
    }
  };

  const handleActivate = () => {
    Modal.confirm({
      title: intl.formatMessage({
        id: 'public-tender-activate-confirm-title',
      }),
      icon: <ExclamationCircleOutlined />,
      content: intl.formatMessage({
        id: 'public-tender-activate-confirm-description',
      }),
      okText: intl.formatMessage({ id: 'activate' }),
      cancelText: intl.formatMessage({ id: 'cancel' }),
      okButtonProps: {
        disabled: actionsDisabled,
      },
      onOk: () => {
        if (!projectId || !tenderId) return;

        publishTender({
          projectId,
          tenderId,
          action: 'publish',
        });
      },
    });
  };

  const handleDeactivate = () => {
    Modal.confirm({
      title: intl.formatMessage({
        id: 'public-tender-deactivate-confirm-title',
      }),
      icon: <ExclamationCircleOutlined />,
      content: intl.formatMessage({
        id: 'public-tender-deactivate-confirm-description',
      }),
      okText: intl.formatMessage({ id: 'de-activate' }),
      cancelText: intl.formatMessage({ id: 'cancel' }),
      okType: 'danger',
      okButtonProps: {
        disabled: actionsDisabled,
      },
      onOk: () => {
        if (!projectId || !tenderId) return;

        unpublishTender({
          projectId,
          tenderId,
        });
      },
    });
  };

  const handleReactivate = () => {
    Modal.confirm({
      title: intl.formatMessage({
        id: 'public-tender-reactivate-confirm-title',
      }),
      icon: <ExclamationCircleOutlined />,
      content: intl.formatMessage({
        id: 'public-tender-reactivate-confirm-description',
      }),
      okText: intl.formatMessage({ id: 're-activate' }),
      cancelText: intl.formatMessage({ id: 'cancel' }),
      okButtonProps: {
        disabled: actionsDisabled,
      },
      onOk: () => {
        if (!projectId || !tenderId) return;

        publishTender({
          projectId,
          tenderId,
          action: 'publish',
        });
      },
    });
  };

  return (
    <div css={styles.container}>
      <div css={styles.header}>
        <Title level={5} css={styles.title}>
          {intl.formatMessage({ id: 'public-tender' })}
        </Title>

        <Text type="secondary">
          {intl.formatMessage({ id: 'public-tender-description' })}
        </Text>
      </div>

      <Alert
        css={styles.status}
        type={
          !hasPublicResource ? 'info' : isPublicActive ? 'success' : 'warning'
        }
        showIcon
        message={
          !hasPublicResource
            ? intl.formatMessage({ id: 'public-tender-not-published' })
            : isPublicActive
            ? intl.formatMessage({ id: 'public-tender-active' })
            : intl.formatMessage({ id: 'public-tender-deactivated' })
        }
        description={
          !hasPublicResource
            ? intl.formatMessage({
                id: 'public-tender-not-published-description',
              })
            : isPublicActive
            ? intl.formatMessage({ id: 'public-tender-active-description' })
            : intl.formatMessage({
                id: 'public-tender-deactivated-description',
              })
        }
      />

      {publicLink && (
        <div css={styles.linkSection}>
          <div css={styles.linkHeader}>
            <div css={styles.linkIcon}>
              <LinkOutlined />
            </div>

            <div css={styles.linkTitleBlock}>
              <Text strong css={styles.linkTitle}>
                {intl.formatMessage({ id: 'public-tender-link' })}
              </Text>

              <Text type="secondary" css={styles.linkDescription}>
                {intl.formatMessage({ id: 'public-tender-link-description' })}
              </Text>
            </div>
          </div>

          <div css={styles.linkControl}>
            <textarea
              ref={linkTextareaRef}
              css={styles.linkTextarea}
              value={publicLink}
              readOnly
              rows={2}
              onClick={(e) => e.currentTarget.select()}
              onFocus={(e) => e.target.select()}
            />

            <Button
              type="primary"
              icon={<CopyOutlined />}
              onClick={handleCopyLink}
              css={styles.copyButton}>
              {intl.formatMessage({ id: 'copy' })}
            </Button>
          </div>
        </div>
      )}

      <div css={styles.actions}>
        <Space>
          {!hasPublicResource && (
            <Button
              type="primary"
              loading={isPublishing}
              disabled={actionsDisabled}
              onClick={handleActivate}>
              {intl.formatMessage({ id: 'activate' })}
            </Button>
          )}

          {isPublicActive && (
            <Button
              danger
              loading={isUnpublishing}
              disabled={actionsDisabled}
              onClick={handleDeactivate}>
              {intl.formatMessage({ id: 'de-activate' })}
            </Button>
          )}

          {isPublicDeactivated && (
            <Button
              type="primary"
              loading={isPublishing}
              disabled={actionsDisabled}
              onClick={handleReactivate}>
              {intl.formatMessage({ id: 're-activate' })}
            </Button>
          )}
        </Space>
      </div>
    </div>
  );
};

const styleFn = () => ({
  container: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: 20,
  },
  header: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: 4,
  },
  title: {
    margin: 0,
  },
  status: {
    marginBottom: 0,
  },
  linkSection: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: 14,
    padding: 16,
    border: '1px solid #e8e8e8',
    borderRadius: 8,
    background: '#fafafa',
  },
  linkHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
  },
  linkIcon: {
    width: 34,
    height: 34,
    minWidth: 34,
    borderRadius: 8,
    background: '#eef5ff',
    color: '#2f80ed',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 17,
  },
  linkTitleBlock: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: 2,
  },
  linkTitle: {
    fontSize: 15,
    lineHeight: '20px',
  },
  linkDescription: {
    fontSize: 13,
    lineHeight: '18px',
  },
  linkControl: {
    display: 'flex',
    gap: 10,
    alignItems: 'stretch',
  },
  linkTextarea: {
    flex: 1,
    height: 60,
    minHeight: 60,
    maxHeight: 60,
    boxSizing: 'border-box' as const,
    resize: 'none' as const,
    overflow: 'hidden',
    padding: '10px 12px',
    border: '1px dashed #b8c2d6',
    borderRadius: 6,
    fontSize: 13,
    lineHeight: '18px',
    color: '#333',
    outline: 'none',
    background: '#fff',
    cursor: 'pointer',
    scrollbarWidth: 'none' as const,
    '&::-webkit-scrollbar': {
      display: 'none',
    },
  },
  copyButton: {
    minWidth: 112,
    height: 60,
    boxSizing: 'border-box' as const,
    borderRadius: 6,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  actions: {
    display: 'flex',
    justifyContent: 'flex-end',
  },
});
